import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useLogoutMutation } from '@/entities/auth';
import { useNavigator } from '@/shared/navigation';

/**
 * 로그아웃 인터랙션 합성 담당 — 헤드리스 훅.
 *
 * entity(`useLogoutMutation`)는 BE 호출만, 본 훅이 side effect 오케스트레이션을 담당:
 * **로컬 세션을 즉시 종료**(store clear + query cache clear + 로그인 이동)하고 BE 로그아웃은
 * best-effort 로 발사만 한다 — 응답을 기다리지 않고, 실패·무응답은 무시한다(UX 가 API 에 안 묶임).
 * 의도적 세션 종료라 `goToLogin()` 은 redirectUrl 없이 — 복귀하지 않는다. 끝낼 세션(access token)이
 * 없으면 BE 호출을 생략하고 정리만 한다 — 헛 401 방지.
 *
 * BE 호출은 즉시 clear 되기 _전_ 스냅샷 토큰을 mutate 변수로 넘겨 발사한다 — clear 후 async 로 실행돼도
 * 요청이 유효 토큰을 유지한다(그래야 BE 가 세션 주체를 식별). transport 인터셉터가 이 헤더를 보존한다.
 *
 * `canLogout` 은 reactive — 끝낼 세션(토큰)이 있을 때만 true. 호출처(예: StandaloneFooter)가
 * 노출 여부 게이트에 사용한다. 토큰이 사라지면 즉시 false 가 되어 버튼이 사라진다.
 */
export function useLogout() {
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const clear = useAuthStore((s) => s.clear);
  const canLogout = useAuthStore((s) => !!s.accessToken);
  const { mutate } = useLogoutMutation();

  const logout = () => {
    // 즉시 clear 하기 전에 토큰을 스냅샷 — BE 발사가 유효 토큰을 유지하도록 mutate 변수로 넘긴다.
    const accessToken = useAuthStore.getState().accessToken;
    // BE 로그아웃 best-effort 발사(응답 대기 안 함, 실패 무시). 끝낼 세션이 없으면 생략 — 헛 401 방지.
    if (accessToken) {
      mutate(accessToken);
    }
    // 로컬 세션 즉시 종료 + 로그인 이동 — API 완료를 기다리지 않는다.
    clear();
    queryClient.clear();
    navigator.goToLogin();
  };

  return { logout, canLogout };
}
